import { useEffect, useMemo, useState } from 'react';
import { DownloadOutlined, FilePdfOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Empty, Select, Tag } from 'antd';
import { api, apiUrl, getToken } from '../api';
import { Bone, ErrorNotice, PageHeader, Panel } from '../components';
import { formatDateTime, useLoad } from '../lib';

const BOOK = 'calculation-audit.pdf';

/** A document of a version fetched with the admin session, as an object URL the viewer can show. */
async function fetchDocument(version, file) {
    const response = await fetch(apiUrl(`/v1/admin/calc-specs/${version}/${file}`), { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!response.ok)
        throw new Error('The document could not be loaded');
    return URL.createObjectURL(await response.blob());
}

/**
 * Calculation audit: the formulas and flow of every accounting and statutory report, one PDF per report, one
 * folder per app version. The list on the left groups the reports; the one chosen opens on the right. A report
 * changed since the previous version is marked, and the version's changes are listed under the viewer.
 */
export function CalculationsPage() {
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/calc-specs'));
    const versions = useMemo(() => data?.versions ?? [], [data]);
    const [versionKey, setVersionKey] = useState('');
    const version = versions.find((entry) => entry.version === versionKey) ?? versions[0];
    const [specId, setSpecId] = useState('');
    const [url, setUrl] = useState('');
    const [viewError, setViewError] = useState('');
    const spec = version?.specs.find((entry) => entry.id === specId) ?? version?.specs[0];
    const file = spec ? `${spec.id}.pdf` : BOOK;
    const groups = useMemo(() => {
        const map = new Map();
        for (const entry of version?.specs ?? []) map.set(entry.group, [...(map.get(entry.group) ?? []), entry]);
        return [...map.entries()];
    }, [version]);
    useEffect(() => {
        if (!version)
            return undefined;
        let current = true;
        let objectUrl = '';
        setViewError('');
        fetchDocument(version.version, file)
            .then((next) => {
                if (!current)
                    return URL.revokeObjectURL(next);
                objectUrl = next;
                setUrl(next);
            })
            .catch((err) => current && setViewError(err.message));
        return () => {
            current = false;
            if (objectUrl)
                URL.revokeObjectURL(objectUrl);
        };
    }, [version, file]);
    const download = async (name) => {
        if (!version)
            return;
        const href = await fetchDocument(version.version, name);
        const link = document.createElement('a');
        link.href = href;
        link.download = `kivonex-${version.version}-${name}`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(href), 10000);
    };
    const changed = new Set(version?.changes?.changed ?? []);
    const added = new Set(version?.changes?.added ?? []);
    return (<>
      <PageHeader title="Calculation audit" description="How every accounting and statutory report is worked out: the figures it reads, the steps, the formulas and how to check them by hand. One document per report, one set per app version." actions={<>
            <Select value={version?.version} onChange={setVersionKey} style={{ minWidth: 200 }} options={versions.map((entry) => ({ value: entry.version, label: `App ${entry.version} · ${formatDateTime(entry.generatedAt)}` }))} placeholder="Version"/>
            <Button icon={<DownloadOutlined />} onClick={() => download(BOOK)} disabled={!version}>Whole book (PDF)</Button>
            <Button icon={<ReloadOutlined />} onClick={reload}>Refresh</Button>
          </>}/>
      {error && <ErrorNotice error={error}/>}
      {!loading && !versions.length && (<Panel>
          <Empty description="No calculation documents yet. Run npm run calc-specs in kivonexclient and push the backend repo."/>
        </Panel>)}
      {version && (<div className="calc-layout">
          <Panel title="Reports" description={`${version.specs.length} documents · generated ${formatDateTime(version.generatedAt)}`} flush>
            <ul className="calc-list">
              {groups.map(([group, specs]) => (<li key={group}>
                  <div className="calc-group">{group}</div>
                  {specs.map((entry) => (<button type="button" key={entry.id} className={entry.id === (spec?.id ?? '') ? 'calc-item is-on' : 'calc-item'} onClick={() => setSpecId(entry.id)}>
                      <FilePdfOutlined />
                      <span>{entry.title}</span>
                      <small>rev {entry.rev}</small>
                      {added.has(entry.id) ? <Tag color="green">new</Tag> : changed.has(entry.id) ? <Tag color="orange">changed</Tag> : null}
                    </button>))}
                </li>))}
            </ul>
          </Panel>
          <div className="calc-main">
            <Panel title={spec?.title ?? 'Calculation audit'} description={spec ? `Revision ${spec.rev} · ${spec.screen ?? ''}` : ''} extra={<Button icon={<DownloadOutlined />} onClick={() => download(file)} size="small">This PDF</Button>} flush>
              {viewError ? <div className="panel-body"><ErrorNotice error={viewError}/></div> : url ? <iframe title={spec?.title ?? 'document'} src={url} className="calc-viewer"/> : <div className="panel-body"><Bone height={320}/></div>}
            </Panel>
            <Panel title={`What changed in app ${version.version}`} description={version.changes?.previous ? `Against app ${version.changes.previous}` : 'First set of documents: everything is new'}>
              {version.changes?.previous && !changed.size && !added.size && !(version.changes.removed ?? []).length && <p className="cell-sub">No calculation changed between the two versions.</p>}
              <ul className="calc-changes">
                {[...added].map((id) => <li key={`a-${id}`}><Tag color="green">new</Tag> {version.specs.find((entry) => entry.id === id)?.title ?? id}</li>)}
                {[...changed].map((id) => {
                    const entry = version.specs.find((item) => item.id === id);
                    return <li key={`c-${id}`}><Tag color="orange">changed</Tag> {entry?.title ?? id}{entry?.lastChange ? <span className="cell-sub"> · rev {entry.rev}: {entry.lastChange}</span> : null}</li>;
                })}
                {(version.changes?.removed ?? []).map((id) => <li key={`r-${id}`}><Tag>removed</Tag> {id}</li>)}
              </ul>
            </Panel>
          </div>
        </div>)}
    </>);
}
